/* eslint-disable react/prop-types */
import React, { useState, useMemo, forwardRef } from 'react'
import { Formik, Field } from 'formik'
import { Form, Clamp } from 'components/atoms'
import { FieldCustom, FieldArrayDndFormik, FieldMarkdown } from 'components/molecules'
import * as Yup from 'yup';
import { useDebounce } from 'react-use'
import { useFormikForwardRef } from 'hooks'
import { linkRegex } from 'services/constants'

const SchemaItem = Yup.object().shape({
  title: Yup.string()
    .min(2, 'Mínimo de 2 caracteres')
    .max(120, 'Máximo de 120 caracteres')
    .required('Campo Obrigatório'),
  content: Yup.string().required('Campo obrigatório'),
  href: Yup.string()
    .matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
    .required('Campo obrigatório')
});

const SchemaMulti = Yup.object().shape({
  data: Yup.array()
    .of(SchemaItem)
    .min(1, 'Mínimo de 1 coluna')
    .max(2, 'Máximo de 2 colunas ultrapassado'),
});

// eslint-disable-next-line react/prop-types
const ItemForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  return (
    <Formik
      ref={formRef}
      initialValues={initialValues}
      validationSchema={SchemaItem}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Título"
          name="title"
          component={FieldCustom}
        />
        <Field
          title="Conteúdo"
          name="content"
          component={FieldMarkdown}
        />
        <Field
          title="Link"
          name="href"
          component={FieldCustom}
        />
      </Form>
    </Formik>
  )
})

const CellContent = ({ value }) => (
  <Clamp>
    {value}
  </Clamp>
)

const MultiColumnsEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)

  const isSingleColumn = useMemo(() => {
    if ('content' in initialProps && typeof initialProps.content === 'string') return true
    if ('data' in initialProps) return false
    return false
  }, [initialProps])

  const initialValues = useMemo(() => {
    const {
      title, content, href, data
    } = initialProps
    if (isSingleColumn) {
      return {
        title,
        content,
        href
      }
    }
    return {
      data
    }
  }, [initialProps, isSingleColumn])

  const handleChange = (newValues) => {
    const newProps = { ...initialProps, ...newValues };

    const isValid = isSingleColumn
      ? SchemaItem.isValidSync(newValues)
      : SchemaMulti.isValidSync(newValues)

    if (isValid) setEditorProps(newProps)
  }

  useDebounce(() => {
    onChange(editorProps);
  }, 500,
  [editorProps]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isSingleColumn ? SchemaItem : SchemaMulti}
      validate={handleChange}
    >
      <Form>
        {isSingleColumn
          ? (
            <>
              <Field
                title="Texto"
                name="title"
                info="Uma frase, uma citação,..."
                component={FieldCustom}
              />
              <Field
                title="Conteúdo"
                name="content"
                component={FieldMarkdown}
              />
              <Field
                title="Link"
                name="href"
                component={FieldCustom}
              />
            </>
          )
          : (
            <Field
              title="Colunas"
              name="data"
              component={FieldArrayDndFormik}
              columns={[
                {
                  accessor: 'title',
                  Cell: CellContent,
                },
                {
                  accessor: 'content',
                  Cell: CellContent,
                },
                {
                  accessor: 'href',
                  Cell: CellContent,
                }
              ]}
              rowKey="title"
              ItemForm={ItemForm}
              newItemText="Nova coluna"
              maxItems={2}
              shouldHideHeaders
            />
          )}

      </Form>
    </Formik>
  );
}

export default MultiColumnsEditor
